import type { CSSProperties } from "react";
import type { PlacedThought } from "../types/adventure";

interface ThoughtCloudProps {
  thought: PlacedThought;
  systemLabel: string;
  systemIcon: string;
  selectable?: boolean;
  selected?: boolean;
  faded?: boolean;
  pinned?: boolean;
  pinSlot?: number;
  onToggleSelect?: (thoughtId: string) => void;
}

function ThoughtContent({
  systemIcon,
  systemLabel,
  text,
}: {
  systemIcon: string;
  systemLabel: string;
  text: string;
}) {
  return (
    <>
      <span className="act-thought__source" aria-hidden>
        {systemIcon} {systemLabel}
      </span>
      <span className="act-thought__text">{text}</span>
    </>
  );
}

export function ThoughtCloud({
  thought,
  systemLabel,
  systemIcon,
  selectable = false,
  selected = false,
  faded = false,
  pinned = false,
  pinSlot = 0,
  onToggleSelect,
}: ThoughtCloudProps) {
  const className = [
    "act-thought",
    !pinned && !faded ? "act-thought--pop" : "",
    thought.overlay ? "act-thought--overlay" : "",
    !pinned && thought.anchor === "left" ? "act-thought--anchor-left" : "",
    !pinned && thought.anchor === "right" ? "act-thought--anchor-right" : "",
    !pinned && thought.anchor === "center" ? "act-thought--anchor-center" : "",
    selectable ? "act-thought--selectable" : "",
    selected ? "act-thought--selected" : "",
    faded ? "act-thought--faded" : "",
    pinned ? "act-thought--pinned" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const { float } = thought;
  const outerStyle = {
    left: pinned ? undefined : `${thought.x}%`,
    top: pinned ? undefined : `${thought.y}%`,
    "--pin-slot": pinSlot,
  } as CSSProperties;

  const floatStyle = {
    "--float-x1": `${float.x1}px`,
    "--float-y1": `${float.y1}px`,
    "--float-x2": `${float.x2}px`,
    "--float-y2": `${float.y2}px`,
    "--float-x3": `${float.x3}px`,
    "--float-y3": `${float.y3}px`,
    "--float-duration": `${float.duration}s`,
    "--float-delay": `${float.delay}s`,
  } as CSSProperties;

  const inner = (
    <span className="act-thought__float" style={floatStyle}>
      <ThoughtContent
        systemIcon={systemIcon}
        systemLabel={systemLabel}
        text={thought.text}
      />
    </span>
  );

  if (selectable && onToggleSelect) {
    return (
      <button
        type="button"
        className={className}
        style={outerStyle}
        onClick={() => onToggleSelect(thought.thoughtId)}
        aria-label={`${selected ? "Deselecteer" : "Selecteer"} helpende gedachte: ${thought.text}`}
        aria-pressed={selected}
      >
        {inner}
      </button>
    );
  }

  return (
    <div className={className} style={outerStyle} aria-live="polite">
      {inner}
    </div>
  );
}
